import { Search } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import type { ResumeContextValue } from "@/app/types/resume";

import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems from "../../../../components/ResumeBuilder/EditorListItems";
import type { EditorListItem } from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips, { type TipsContent } from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { convertArrayToUl } from "../../../../handlers/utilityFunctions";
import { Accomplishment } from "../../../../models/resume/accomplishment";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import accomplishmentListSuggestions from "./accomplishmentList";

interface PatchLedger {
	accomplishments: { data: Parameters<typeof convertArrayToUl>[0] };
}

interface PatchError {
	response: { data: { message: string | string[] } };
}

const accomplishmentsTips: TipsContent = {
	content: [
		{
			bp: false,
			value:
				"Most notable achievements, three to five bullet points. Professional Experience section include more details of the “highlights”",
		},
		{
			bp: true,
			value:
				"Underneath the position information , list your most impressive accomplishments in bullets not paragraphs. Shoot for 3 to 5 bullets.",
		},
		{
			bp: true,
			value:
				"If you only held one or two positions and require more bullets for each position to fill out your resume content → Just make sure the additional accomplishments you highlight are relevant to the job description and carry the momentum you've established in previous bullets",
		},
		{
			bp: true,
			value:
				"Describe your accomplishments in terms of results, not responsibilities: Relevant → to job you're applying for; start with most to least relevant. Relative → want to compare you performance to established targets. Results-oriented → Most quantifiable the better; articulate into tangible value statements [time/cost saved, increased speed/revenue etc.]",
		},
		{
			bp: true,
			value:
				"Recommend leaving off any work experience that took place 15+ years ago. Only exception is if the position is your current role OR the experience is more relevant to position you're applying to than your current role",
		},
	],
};

const Accomplishments = () => {
	const navigate = useNavigate();

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const patchSection = usePatchResumeSection();
	const [cookies] = useCookies(["resume"]);
	const [accomplishmentList, setAccomplishmentList] = useState<EditorListItem[]>([...accomplishmentListSuggestions]);

	const [errors, setErrors] = useState<string[]>([]);

	useEffect(() => {
		errors.map((item) => {
			toast.error(item);
		});
		setErrors([]);
	}, [errors]);

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const [accomplishments, setAccomplishments] = useState(resumeData.extras.accomplishments);

	const [userInput, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	//submission handler
	const addAccomplishments = () => {
		if (accomplishments === "") {
			setErrors((prevState) => {
				return [...prevState, "Please add your accomplishments"];
			});
		}

		const payload = new Accomplishment(accomplishments);
		const request = payload.getAccomplishmentRequest();

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.ACHIEVEMENTS, data: request } as never, {
			onSuccess: successPatchAccomplishment,
			onError: errorPatchAccomplishment,
		});
	};

	function successPatchAccomplishment(ledger: PatchLedger) {
		const resArray = ledger.accomplishments.data;

		const accomplishmentList = convertArrayToUl(resArray);

		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					accomplishments: accomplishmentList,
				},
			};
		});

		navigate("/resume/extras");
	}

	function errorPatchAccomplishment(data: Error) {
		const serverResponse = (data as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
	}, [resumeData]);

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div>
					<p className="mb-3 text-gray-700">Tell us about your accomplishments?</p>

					<div className="mb-4">
						<div className="flex">
							<Input type="text" className="rounded-r-none" placeholder={userInput} />
							<Button type="button" className="rounded-l-none md:px-10" aria-label="Search">
								<Search className="size-4" />
							</Button>
						</div>
					</div>
					<EditorListItems
						editorListItems={accomplishmentList}
						editorContent={accomplishments}
						setEditorContent={setAccomplishments}
						setEditorListItems={setAccomplishmentList}
					/>
				</div>
				<div className="mt-4 flex flex-col justify-end">
					<div className="mb-1 flex justify-between">
						<div />
						<Tips content={accomplishmentsTips} />
					</div>

					<TextEditor
						value={accomplishments}
						setValue={setAccomplishments}
						placeholder="Write about your accomplishments here..."
					/>
				</div>
				<div className="col-span-full mt-4 flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						size="lg"
						onClick={() => {
							navigate("/resume/extras");
						}}
					>
						Cancel
					</Button>
					<Button type="button" onClick={addAccomplishments}>
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Save"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default Accomplishments;
